import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useProjectCsvMappingQuery } from '@/features/projects/hooks/useProjectCsvMapping';
import { validateResultsSpreadsheetFile } from '@/features/quality/sections/resultsFileSpreadsheetValidation';
import {
  isCsvResultsFile,
  isJsonResultsFile,
  isSupportedResultsFile,
  isXmlResultsFile,
  resolveResultsFileSource,
} from '@/features/quality/sections/resultsFileUploadUtils';
import { useResultsFileUploadPolling } from '@/features/quality/sections/useResultsFileUploadPolling';
import { useUploadTagDraftState } from '@/features/quality/sections/useUploadTagDraftState';
import { commitUpload, createUpload, uploadFile } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { getErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import {
  useInvalidateQueries,
  useMutationErrorSnackbar,
  buildUploadResultsInvalidations,
} from '@/shared/hooks';
import { markUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import { Inline, Link, Text } from '@/ui';
import {
  defaultCsvHeaderAliases,
  defaultCsvStatusAliases,
  type CsvHeaderAliases,
  type CsvStatusAliases,
} from '@flowqa/shared';
import type {
  CsvHeaderStatus,
  CsvHeaderValidationResult,
  FileValidationStatus,
  FileValidationResult,
  ResultsFileUploadParams,
  ResultsFileUploadState,
  UploadMutationData,
} from '@/features/quality/sections/useResultsFileUpload.types';

const stripErrorId = (message: string): string => message.replace(/\s*\(Error ID: [^)]+\)/, '');

export const useResultsFileUpload = ({
  selectedProjectId,
  selectedOrganisationId,
  onRefresh,
}: ResultsFileUploadParams): ResultsFileUploadState => {
  const [uploadProjectId, setUploadProjectId] = useState(selectedProjectId ?? '');
  const {
    uploadTags,
    uploadTagColors,
    uploadTagsInput,
    uploadTagValues,
    setUploadTags,
    setUploadTagColors,
    setUploadTagsInput,
    resetUploadTags,
  } = useUploadTagDraftState();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [csvHeaderStatusByName, setCsvHeaderStatusByName] = useState<
    Record<string, CsvHeaderStatus | undefined>
  >({});
  const [csvHeaderReasonByName, setCsvHeaderReasonByName] = useState<
    Record<string, string | null | undefined>
  >({});
  const [fileValidationStatusByName, setFileValidationStatusByName] = useState<
    Record<string, FileValidationStatus | undefined>
  >({});
  const [fileValidationReasonByName, setFileValidationReasonByName] = useState<
    Record<string, string | null | undefined>
  >({});
  const mutationError = useMutationErrorSnackbar();
  const invalidateQueries = useInvalidateQueries();
  const { notify } = useNotifications();
  const sharedUploadInvalidations = useMemo(
    () =>
      buildUploadResultsInvalidations({
        selectedOrganisationId,
        selectedProjectId: selectedProjectId ?? null,
        uploadProjectId: uploadProjectId || null,
      }),
    [selectedOrganisationId, selectedProjectId, uploadProjectId],
  );
  const invalidateUploadRelatedTables = useCallback(async (): Promise<void> => {
    await invalidateQueries(sharedUploadInvalidations);
  }, [invalidateQueries, sharedUploadInvalidations]);
  const selectedProjectIdRef = useRef<string | null>(selectedProjectId ?? null);
  const mutationErrorRef = useRef(mutationError);
  const csvValidationTokensRef = useRef<Record<string, number>>({});
  const fileValidationTokensRef = useRef<Record<string, number>>({});
  const uploadFilesRef = useRef<File[]>([]);
  const activeProjectId =
    selectedProjectId ?? (uploadProjectId.length > 0 ? uploadProjectId : null);
  const csvMappingQuery = useProjectCsvMappingQuery(activeProjectId, { enabled: uploadOpen });
  const resolvedHeaderAliases = useMemo<CsvHeaderAliases>(() => {
    const source = csvMappingQuery.data?.headerAliases ?? defaultCsvHeaderAliases;
    const normalizeList = (aliases: string[] | undefined): string[] =>
      (aliases ?? []).map((alias) => alias.trim().toLowerCase()).filter(Boolean);
    return {
      suite: normalizeList(source.suite),
      test: normalizeList(source.test),
      status: normalizeList(source.status),
      ...(source.duration ? { duration: normalizeList(source.duration) } : {}),
      ...(source.timestamp ? { timestamp: normalizeList(source.timestamp) } : {}),
    };
  }, [csvMappingQuery.data]);
  const resolvedStatusAliases = useMemo<CsvStatusAliases>(() => {
    const source = csvMappingQuery.data?.statusAliases ?? defaultCsvStatusAliases;
    const normalizeList = (aliases: string[] | undefined): string[] =>
      (aliases ?? []).map((alias) => alias.trim()).filter(Boolean);
    return {
      passed: normalizeList(source.passed),
      failed: normalizeList(source.failed),
      errored: normalizeList(source.errored),
      skipped: normalizeList(source.skipped),
      untested: normalizeList(source.untested),
    };
  }, [csvMappingQuery.data]);
  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId ?? null;
  }, [selectedProjectId]);
  useEffect(() => {
    mutationErrorRef.current = mutationError;
  }, [mutationError]);
  useEffect(() => {
    uploadFilesRef.current = uploadFiles;
  }, [uploadFiles]);
  useEffect(() => {
    setSelectedFiles((current) => {
      const next = new Set(current);
      uploadFilesRef.current.forEach((file) => {
        if (!isCsvResultsFile(file)) return;
        if (csvHeaderStatusByName[file.name] !== 'valid') return;
        if (next.has(file.name)) return;
        next.add(file.name);
      });
      return next;
    });
  }, [csvHeaderStatusByName, setSelectedFiles]);

  const bumpCsvValidationToken = useCallback((fileName: string): number => {
    const currentToken = csvValidationTokensRef.current[fileName] ?? 0;
    const nextToken = currentToken + 1;
    csvValidationTokensRef.current[fileName] = nextToken;
    return nextToken;
  }, []);
  const bumpFileValidationToken = useCallback((fileName: string): number => {
    const currentToken = fileValidationTokensRef.current[fileName] ?? 0;
    const nextToken = currentToken + 1;
    fileValidationTokensRef.current[fileName] = nextToken;
    return nextToken;
  }, []);

  const resolveCsvHeaderStatus = useCallback(
    async (file: File): Promise<CsvHeaderValidationResult> => {
      return validateResultsSpreadsheetFile(file, {
        headerAliases: resolvedHeaderAliases,
        statusAliases: resolvedStatusAliases,
      });
    },
    [resolvedHeaderAliases, resolvedStatusAliases],
  );
  const resolveFileValidationStatus = useCallback(
    async (file: File): Promise<FileValidationResult | null> => {
      if (isJsonResultsFile(file)) {
        try {
          const content = await file.text();
          if (!content.trim()) {
            return { status: 'invalid', reason: 'JSON file is empty.' };
          }
          JSON.parse(content);
          return { status: 'valid', reason: null };
        } catch {
          return { status: 'invalid', reason: 'Invalid JSON.' };
        }
      }
      if (isXmlResultsFile(file)) {
        try {
          const content = await file.text();
          if (!content.trim()) {
            return { status: 'invalid', reason: 'XML file is empty.' };
          }
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, 'application/xml');
          const parserErrors = doc.getElementsByTagName('parsererror');
          if (parserErrors.length > 0) {
            return { status: 'invalid', reason: 'Invalid XML.' };
          }
          return { status: 'valid', reason: null };
        } catch {
          return { status: 'invalid', reason: 'Invalid XML.' };
        }
      }
      return null;
    },
    [],
  );
  const revalidateCsvFiles = useCallback(() => {
    const csvFiles = uploadFilesRef.current.filter((file) => isCsvResultsFile(file));
    if (csvFiles.length === 0) return;
    setCsvHeaderStatusByName((current) => {
      const next = { ...current };
      csvFiles.forEach((file) => {
        next[file.name] = 'valid';
      });
      return next;
    });
    setCsvHeaderReasonByName((current) => {
      const next = { ...current };
      csvFiles.forEach((file) => {
        next[file.name] = null;
      });
      return next;
    });
    setSelectedFiles((current) => {
      const next = new Set(current);
      csvFiles.forEach((file) => {
        next.add(file.name);
      });
      return next;
    });
    csvFiles.forEach((file) => {
      const token = bumpCsvValidationToken(file.name);
      void resolveCsvHeaderStatus(file).then((result) => {
        setCsvHeaderStatusByName((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          return { ...current, [file.name]: result.status };
        });
        setSelectedFiles((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          const next = new Set(current);
          if (result.status === 'invalid') {
            next.delete(file.name);
            return next;
          }
          next.add(file.name);
          return next;
        });
        setCsvHeaderReasonByName((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          const fallbackReason = result.status === 'invalid' ? 'Invalid CSV/XLSX file.' : null;
          return { ...current, [file.name]: result.reason ?? fallbackReason };
        });
      });
    });
  }, [
    activeProjectId,
    bumpCsvValidationToken,
    csvMappingQuery.data,
    csvMappingQuery.isPending,
    resolveCsvHeaderStatus,
    setCsvHeaderReasonByName,
  ]);
  useEffect(() => {
    if (uploadOpen) revalidateCsvFiles();
  }, [activeProjectId, revalidateCsvFiles, uploadOpen]);
  const pollResultsFile = useResultsFileUploadPolling({
    selectedOrganisationId,
    onRefresh,
    invalidateUploadRelatedTables,
    notify,
  });
  const resolveSource = useCallback(resolveResultsFileSource, []);
  const isSupportedFile = useCallback(
    (file: File) => {
      if (!isSupportedResultsFile(file)) return false;
      if (isCsvResultsFile(file)) {
        const headerStatus = csvHeaderStatusByName[file.name];
        return headerStatus !== 'invalid';
      }
      if (isJsonResultsFile(file) || isXmlResultsFile(file)) {
        const status = fileValidationStatusByName[file.name];
        return status !== 'invalid';
      }
      return true;
    },
    [csvHeaderStatusByName, fileValidationStatusByName],
  );
  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setUploadFiles((current) => {
        const byName = new Map(current.map((file) => [file.name, file]));
        files.forEach((file) => {
          byName.set(file.name, file);
        });
        return Array.from(byName.values());
      });
      setSelectedFiles((current) => {
        const next = new Set(current);
        files.forEach((file) => {
          next.add(file.name);
        });
        return next;
      });
      const csvFiles = files.filter((file) => isCsvResultsFile(file));
      if (csvFiles.length > 0) {
        setCsvHeaderStatusByName((current) => {
          const next = { ...current };
          csvFiles.forEach((file) => {
            next[file.name] = 'valid';
          });
          return next;
        });
        setSelectedFiles((current) => {
          const next = new Set(current);
          csvFiles.forEach((file) => {
            next.add(file.name);
          });
          return next;
        });
        setCsvHeaderReasonByName((current) => {
          const next = { ...current };
          csvFiles.forEach((file) => {
            next[file.name] = null;
          });
          return next;
        });
        csvFiles.forEach((file) => {
          const token = bumpCsvValidationToken(file.name);
          void resolveCsvHeaderStatus(file).then((result) => {
            setCsvHeaderStatusByName((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              return { ...current, [file.name]: result.status };
            });
            setSelectedFiles((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              const next = new Set(current);
              if (result.status === 'invalid') {
                next.delete(file.name);
                return next;
              }
              next.add(file.name);
              return next;
            });
            setCsvHeaderReasonByName((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              const fallbackReason = result.status === 'invalid' ? 'Invalid CSV/XLSX file.' : null;
              return { ...current, [file.name]: result.reason ?? fallbackReason };
            });
          });
        });
      }

      const jsonXmlFiles = files.filter(
        (file) => isJsonResultsFile(file) || isXmlResultsFile(file),
      );
      if (jsonXmlFiles.length === 0) return;
      setFileValidationStatusByName((current) => {
        const next = { ...current };
        jsonXmlFiles.forEach((file) => {
          next[file.name] = 'valid';
        });
        return next;
      });
      setFileValidationReasonByName((current) => {
        const next = { ...current };
        jsonXmlFiles.forEach((file) => {
          next[file.name] = null;
        });
        return next;
      });
      jsonXmlFiles.forEach((file) => {
        const token = bumpFileValidationToken(file.name);
        void resolveFileValidationStatus(file).then((result) => {
          if (!result) return;
          setFileValidationStatusByName((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            return { ...current, [file.name]: result.status };
          });
          setSelectedFiles((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            const next = new Set(current);
            if (result.status === 'invalid') {
              next.delete(file.name);
              return next;
            }
            next.add(file.name);
            return next;
          });
          setFileValidationReasonByName((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            return { ...current, [file.name]: result.reason ?? null };
          });
        });
      });
    },
    [
      bumpCsvValidationToken,
      bumpFileValidationToken,
      resolveCsvHeaderStatus,
      resolveFileValidationStatus,
    ],
  );

  const selectedUploadFiles = useMemo(
    () => uploadFiles.filter((file) => selectedFiles.has(file.name) && isSupportedFile(file)),
    [isSupportedFile, selectedFiles, uploadFiles],
  );
  const closeUploadDialog = useCallback(() => {
    setUploadOpen(false);
    setUploadError(null);
    setUploadProjectId(selectedProjectIdRef.current ?? '');
    resetUploadTags();
    setUploadFiles([]);
    setSelectedFiles(new Set());
    setCsvHeaderStatusByName({});
    setCsvHeaderReasonByName({});
    setFileValidationStatusByName({});
    setFileValidationReasonByName({});
    csvValidationTokensRef.current = {};
    fileValidationTokensRef.current = {};
    mutationErrorRef.current.clearError();
  }, [resetUploadTags]);
  const openUploadDialog = useCallback(() => {
    setUploadOpen(true);
    setUploadError(null);
    mutationErrorRef.current.clearError();
    if (selectedProjectIdRef.current) {
      setUploadProjectId(selectedProjectIdRef.current);
    }
  }, []);
  const uploadMutation = useMutation<
    UploadMutationData,
    unknown,
    { files: File[]; projectId: string; tags: string[]; colors: TagColor[] }
  >({
    mutationFn: async ({ files, projectId, tags, colors }) => {
      const tagColorUpdates = tags
        .map((name, index) => ({
          name,
          color: colors[index] ?? 'neutral',
        }))
        .filter((item) => item.name.length > 0);
      if (tagColorUpdates.length > 0) {
        await Promise.all(
          tagColorUpdates.map(async (item) => {
            await updateTagColor(item, selectedOrganisationId ?? undefined);
          }),
        );
      }
      let count = 0;
      const results: Array<{
        fileName: string;
        uploadId?: string;
        resultsFileSlug?: string | null;
        error?: string;
      }> = [];
      for (const file of files) {
        let uploadId: string | undefined;
        try {
          const createResponse = await createUpload(
            projectId,
            {
              source: resolveSource(file),
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
              tags: tags.length > 0 ? tags : undefined,
            },
            selectedOrganisationId,
          );
          uploadId = createResponse.uploadId;
          markUploadToastIntent(uploadId);
          await uploadFile(createResponse.uploadId, file, selectedOrganisationId);
          const committed = await commitUpload(createResponse.uploadId, selectedOrganisationId);
          results.push({
            fileName: file.name,
            uploadId,
            resultsFileSlug: committed.resultsFileSlug ?? null,
          });
          count += 1;
        } catch (err) {
          const reason = getErrorMessage(err);
          results.push({
            fileName: file.name,
            uploadId,
            error: stripErrorId(reason),
          });
        }
      }
      return { count, results };
    },
    onSuccess: async (result) => {
      setUploadOpen(false);
      setUploadFiles([]);
      setSelectedFiles(new Set());
      resetUploadTags();
      setUploadError(null);
      setCsvHeaderStatusByName({});
      setCsvHeaderReasonByName({});
      setFileValidationStatusByName({});
      setFileValidationReasonByName({});
      csvValidationTokensRef.current = {};
      fileValidationTokensRef.current = {};
      mutationError.clearError();
      await invalidateUploadRelatedTables();
      result.results.forEach((entry) => {
        if (entry.error) {
          const failureHref = entry.uploadId ? `/uploads/${entry.uploadId}` : null;
          notify({
            message: (
              <Inline gap="sm" align="center">
                <Text role="body" component="span">
                  {entry.fileName}: {entry.error}
                </Text>
                {failureHref ? (
                  <Link component={RouterLink} to={failureHref} underline="always" color="inherit">
                    View
                  </Link>
                ) : null}
              </Inline>
            ),
            tone: 'red',
          });
          return;
        }
        if (entry.uploadId) {
          pollResultsFile(entry.uploadId);
        }
      });
      onRefresh?.();
    },
    onError: (err) => {
      mutationError.setError(err);
    },
  });
  const disableUpload =
    uploadFiles.length === 0 ||
    selectedUploadFiles.length === 0 ||
    (!selectedProjectId && !uploadProjectId);

  const startUpload = useCallback(
    (projectId: string, filesToUpload: File[], tags: string[], colors: TagColor[]) => {
      const fileLabel = filesToUpload.length === 1 ? 'file' : 'files';
      notify({
        message: getToastMessage('upload.filesUploading', {
          count: filesToUpload.length,
          fileLabel,
        }),
        tone: 'blue',
      });
      uploadMutation.mutate({ files: filesToUpload, projectId, tags, colors });
    },
    [notify, uploadMutation],
  );
  return {
    uploadOpen,
    uploadProjectId,
    uploadTags,
    uploadTagColors,
    uploadTagsInput,
    uploadTagValues,
    uploadFiles,
    selectedFiles,
    uploadError,
    selectedUploadFiles,
    uploadMutation,
    disableUpload,
    isSupportedFile,
    csvHeaderStatusByName,
    csvHeaderReasonByName,
    fileValidationStatusByName,
    fileValidationReasonByName,
    handleFilesSelected,
    openUploadDialog,
    closeUploadDialog,
    setUploadProjectId,
    setUploadTagsInput,
    setUploadTags,
    setUploadTagColors,
    setUploadFiles,
    setSelectedFiles,
    setUploadError,
    setCsvHeaderStatusByName,
    setCsvHeaderReasonByName,
    setFileValidationStatusByName,
    setFileValidationReasonByName,
    startUpload,
  };
};
